import { Link } from 'react-router-dom';
import { Scale, Users, Star, Shield, RefreshCw } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';

const AboutPage = () => {
  return (
    <div className="py-12" data-testid="about-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'Über Uns', href: '/ueber-uns' }]} />

        <div className="max-w-4xl">
          <header className="mb-12">
            <h1 className="mb-6">Über ReviewLex — Ihr unabhängiger Ratgeber für internationale Firmengründung</h1>
            <p className="text-xl text-slate-600">
              Wir helfen deutschsprachigen Unternehmern, fundierte Entscheidungen bei der Gründung 
              von Unternehmen im Ausland zu treffen.
            </p>
          </header>

          <article className="prose-reviewlex">
            <section className="mb-12">
              <h2>Wer wir sind</h2>
              <p>
                ReviewLex.de ist ein unabhängiges deutschsprachiges Informationsportal für internationale 
                Firmengründung. Unser Redaktionsteam besteht aus Experten mit Erfahrung in Unternehmensrecht, 
                internationalem Steuerrecht und digitaler Wirtschaft.
              </p>
              <p>
                Wir haben ReviewLex gegründet, weil wir selbst vor der Herausforderung standen, im Ausland 
                ein Unternehmen zu gründen. Die verfügbaren Informationen waren verstreut, oft veraltet, 
                oder von Anbietern geschrieben, die ihre eigenen Services verkaufen wollten. 
              </p>
              <p>
                Unser Ziel ist es, die zuverlässigste und umfassendste deutschsprachige Ressource für 
                LLC-Gründungen in den USA, LTD-Gründungen im UK und Corporation-Gründungen in Kanada 
                zu schaffen.
              </p>
            </section>

            <section className="mb-12">
              <h2>Unsere Mission</h2>
              <p>
                Wir möchten deutschsprachigen Unternehmern dabei helfen, fundierte Entscheidungen bei der 
                Gründung von Unternehmen im Ausland zu treffen. Das bedeutet:
              </p>
              <ul>
                <li>
                  <strong>Unabhängige Bewertungen:</strong> Unsere Empfehlungen basieren auf echten Tests, 
                  nicht auf Provisionen.
                </li>
                <li>
                  <strong>Aktuelle Informationen:</strong> Wir aktualisieren unsere Inhalte regelmäßig, 
                  um Änderungen in Gesetzen und Preisen zu reflektieren.
                </li>
                <li>
                  <strong>Praktische Anleitungen:</strong> Keine theoretischen Abhandlungen, sondern 
                  Schritt-für-Schritt-Guides, die Sie direkt umsetzen können.
                </li>
                <li>
                  <strong>Transparenz:</strong> Wir offenbaren unsere Finanzierungsquellen und erklären, 
                  wie unsere Bewertungen zustande kommen.
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2>So testen wir</h2>
              <p>
                Jeder Anbieter wird nach unserem standardisierten Bewertungssystem geprüft. Wir testen 
                den gesamten Gründungsprozess selbst und bewerten:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6 my-8">
                <div className="card-flat p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-sm">
                      <Star className="w-5 h-5 text-teal-600" />
                    </div>
                    <h4 className="font-heading font-bold text-navy-900 mb-0">Preis-Leistung</h4>
                  </div>
                  <p className="text-slate-600 text-sm mb-0">
                    Was kostet der Service? Was ist inklusive? Gibt es versteckte Gebühren?
                  </p>
                </div>

                <div className="card-flat p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-sm">
                      <Users className="w-5 h-5 text-teal-600" />
                    </div>
                    <h4 className="font-heading font-bold text-navy-900 mb-0">Kundenservice</h4>
                  </div>
                  <p className="text-slate-600 text-sm mb-0">
                    Wie schnell und kompetent werden Anfragen beantwortet?
                  </p>
                </div>

                <div className="card-flat p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-sm">
                      <Shield className="w-5 h-5 text-teal-600" />
                    </div>
                    <h4 className="font-heading font-bold text-navy-900 mb-0">Datenschutz</h4>
                  </div>
                  <p className="text-slate-600 text-sm mb-0">
                    Wird die Privatsphäre des Gründers geschützt? Welche Daten werden öffentlich?
                  </p>
                </div>

                <div className="card-flat p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-sm">
                      <RefreshCw className="w-5 h-5 text-teal-600" />
                    </div>
                    <h4 className="font-heading font-bold text-navy-900 mb-0">Benutzerfreundlichkeit</h4>
                  </div>
                  <p className="text-slate-600 text-sm mb-0">
                    Wie einfach ist der Gründungsprozess? Ist das Dashboard übersichtlich?
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2>Redaktionelle Unabhängigkeit</h2>
              <p>
                Unsere Bewertungen sind redaktionell unabhängig. Wir finanzieren uns teilweise über 
                Affiliate-Partnerschaften mit Gründungsdienstleistern wie Northwest Registered Agent. 
                Das bedeutet:
              </p>
              <ul>
                <li>
                  Wenn Sie über einen unserer Links einen Service kaufen, erhalten wir möglicherweise 
                  eine Provision — ohne zusätzliche Kosten für Sie.
                </li>
                <li>
                  <strong>Diese Provisionen haben keinen Einfluss auf unsere Bewertungen.</strong> Ein 
                  Anbieter kann nicht für eine bessere Bewertung bezahlen.
                </li>
                <li>
                  Wir empfehlen nur Services, die wir selbst getestet haben und von deren Qualität 
                  wir überzeugt sind.
                </li>
              </ul>
              <p>
                Mehr Details finden Sie in unserer{' '}
                <Link to="/affiliate-offenlegung" className="text-teal-600 hover:text-teal-700">
                  Affiliate-Offenlegung
                </Link>.
              </p>
            </section>

            <section className="mb-12">
              <h2>Unser Team</h2>
              <p>
                Hinter ReviewLex steht ein kleines, aber spezialisiertes Team:
              </p>

              <div className="space-y-6 my-8">
                <div className="card-flat p-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-2">Chefredaktion</h4>
                  <p className="text-slate-600 text-sm mb-0">
                    Verantwortlich für die redaktionelle Linie, Qualitätskontrolle und Koordination 
                    des Teams. Hintergrund in Wirtschaftsrecht und digitaler Medien.
                  </p>
                </div>

                <div className="card-flat p-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-2">Rechtsrecherche</h4>
                  <p className="text-slate-600 text-sm mb-0">
                    Spezialisiert auf US-Gesellschaftsrecht, UK Companies Act und kanadisches 
                    Unternehmensrecht. Stellt sicher, dass alle rechtlichen Informationen aktuell 
                    und korrekt sind.
                  </p>
                </div>

                <div className="card-flat p-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-2">Content-Team</h4>
                  <p className="text-slate-600 text-sm mb-0">
                    Erstellt und aktualisiert unsere Ratgeber, führt Anbieter-Tests durch und 
                    hält unsere Vergleichstabellen auf dem neuesten Stand.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2>Kontakt</h2>
              <p>
                Haben Sie Fragen, Anregungen oder Feedback? Wir freuen uns von Ihnen zu hören:
              </p>
              <p>
                <strong>E-Mail:</strong>{' '}
                <a href="mailto:kontakt@reviewlex.de" className="text-teal-600 hover:text-teal-700">
                  kontakt@reviewlex.de
                </a>
              </p>
              <p>
                Oder nutzen Sie unser{' '}
                <Link to="/kontakt" className="text-teal-600 hover:text-teal-700">
                  Kontaktformular
                </Link>.
              </p>
              <p className="text-sm text-slate-500 mt-6">
                Bitte beachten Sie, dass wir keine individuelle Rechts- oder Steuerberatung anbieten. 
                Für persönliche rechtliche Fragen wenden Sie sich bitte an einen qualifizierten 
                Anwalt oder Steuerberater.
              </p>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
